import styled from 'styled-components';
import { monthTick, shortDate } from '@entities/ksp/format';
import { Card, Muted, Row, Small } from '../styles';
import { milestonePoints, plural, type MilestonePoint, type PulseInput } from './pulse';

const Track = styled.div`
  position: relative;
  height: 104px;
  margin: 8px 12px 0;
`;
const Axis = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 46px;
  height: 2px;
  background: ${({ theme }) => theme.colors.borderStrong};
`;
const Tick = styled.span<{ $left: number }>`
  position: absolute;
  top: 84px;
  left: ${({ $left }) => $left}%;
  transform: translateX(-50%);
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.muted};
  white-space: nowrap;
`;
const Today = styled.span<{ $left: number }>`
  position: absolute;
  top: 26px;
  height: 42px;
  left: ${({ $left }) => $left}%;
  border-left: 2px dotted ${({ theme }) => theme.colors.text.secondary};
`;
const Diamond = styled.span<{ $left: number; $done: boolean }>`
  position: absolute;
  top: 40px;
  left: ${({ $left }) => $left}%;
  width: 12px;
  height: 12px;
  margin-left: -6px;
  transform: rotate(45deg);
  border: 2px solid
    ${({ theme, $done }) => ($done ? theme.colors.status.success : theme.colors.primary)};
  background: ${({ theme, $done }) => ($done ? theme.colors.status.success : theme.colors.primary)};
`;
const Label = styled.span<{ $left: number; $up: boolean; $align: 'start' | 'middle' | 'end' }>`
  position: absolute;
  ${({ $up }) => ($up ? 'top: 4px;' : 'top: 58px;')}
  left: ${({ $left }) => $left}%;
  transform: translateX(
    ${({ $align }) => ($align === 'start' ? '0' : $align === 'end' ? '-100%' : '-50%')}
  );
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;
const List = styled.ul`
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
`;
const ListItem = styled.li`
  display: grid;
  grid-template-columns: 64px minmax(0, 1fr);
  gap: 10px;
  padding: 7px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 13px;
  &:last-child {
    border-bottom: 0;
  }
`;
const When = styled.span`
  font-weight: 600;
  white-space: nowrap;
`;
const What = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** Близкие по дате вехи — одной отметкой, чтобы подписи не слипались. */
function cluster(points: MilestonePoint[], position: (t: number) => number) {
  const groups: { left: number; points: MilestonePoint[] }[] = [];
  for (const point of points) {
    const left = position(Date.parse(point.date));
    const last = groups[groups.length - 1];
    if (last && left - last.left < 5) last.points.push(point);
    else groups.push({ left, points: [point] });
  }
  return groups;
}

/**
 * Вехи проекта. Договорные — лентой по сроку проекта (с отметкой «сегодня»). Если договорных
 * вех нет — ближайшие вехи графика списком: они обычно в пределах недель, на ленте до конца
 * проекта слиплись бы в одну точку.
 */
export function MilestoneStrip({
  input,
  timezone,
  now,
}: {
  input: PulseInput;
  timezone: string;
  now: Date;
}) {
  const points = milestonePoints(input, now);
  const contract = input.summary?.contract_finish ?? null;
  if (points.length === 0)
    return (
      <Card>
        <h3>Вехи проекта</h3>
        <Muted>Вех пока нет: договорные вехи заводятся в разделе «Вехи».</Muted>
      </Card>
    );
  if (points[0].kind === 'calculated')
    return (
      <Card>
        <Row>
          <h3>Ближайшие вехи графика</h3>
          <Small>договорных вех нет — показаны расчётные вехи графика</Small>
        </Row>
        <List>
          {points.map((point) => (
            <ListItem key={`${point.title}-${point.date}`}>
              <When>{shortDate(point.date, timezone)}</When>
              <What title={point.title}>{point.title}</What>
            </ListItem>
          ))}
        </List>
      </Card>
    );
  const times = [
    now.getTime(),
    ...points.map((p) => Date.parse(p.date)),
    ...(contract ? [Date.parse(contract)] : []),
  ].filter(Number.isFinite);
  const from = Math.min(...times);
  const to = Math.max(...times);
  const span = Math.max(to - from, 86400000);
  const x = (t: number) => 2 + ((t - from) / span) * 96;
  const ticks: number[] = [];
  const first = new Date(from);
  const cursor = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 1));
  const months = Math.max(1, Math.round(span / (30 * 86400000)));
  const step = Math.max(1, Math.ceil(months / 5));
  while (cursor.getTime() <= to) {
    ticks.push(cursor.getTime());
    cursor.setUTCMonth(cursor.getUTCMonth() + step);
  }
  const groups = cluster(points, x);
  return (
    <Card>
      <Row>
        <h3>Договорные вехи</h3>
        <Small>◆ срок по прогнозу · зелёная — достигнута · пунктир — сегодня</Small>
      </Row>
      <Track>
        <Axis />
        {ticks.map((t) => (
          <Tick key={t} $left={x(t)}>
            {monthTick(new Date(t).toISOString(), timezone)}
          </Tick>
        ))}
        <Today $left={x(now.getTime())} title="Сегодня" />
        {groups.map((group, i) => {
          const align = group.left < 12 ? 'start' : group.left > 88 ? 'end' : 'middle';
          const head = group.points[0];
          const title = group.points
            .map((p) => `${shortDate(p.date, timezone)} — ${p.title}${p.achieved ? ' (достигнута)' : ''}`)
            .join('\n');
          return (
            <span key={`${head.title}-${head.date}`}>
              <Diamond $left={group.left} $done={group.points.every((p) => p.achieved)} title={title} />
              <Label $left={group.left} $up={i % 2 === 0} $align={align} title={title}>
                {group.points.length === 1
                  ? `${shortDate(head.date, timezone)} · ${head.title}`
                  : `${shortDate(head.date, timezone)} · ${group.points.length} ${plural(group.points.length, 'веха', 'вехи', 'вех')}`}
              </Label>
            </span>
          );
        })}
      </Track>
    </Card>
  );
}
